import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { http } from "./http";
import { useAuth } from "./AuthContext";
import type { AppNotification, NotificationsResponse } from "../data/types";

const POLL_INTERVAL_MS = 20_000;
const TOAST_DURATION_MS = 6_000;
const MAX_VISIBLE_TOASTS = 3;

type NotificationsCtx = {
  items: AppNotification[];
  unreadCount: number;
  pendingRequests: number;
  isLoading: boolean;

  /** Notiser som just kommit in och ska visas som popup högst upp. */
  toasts: AppNotification[];
  dismissToast: (id: string) => void;

  refresh: () => Promise<void>;
  markAllRead: () => Promise<void>;
};

const Ctx = createContext<NotificationsCtx | null>(null);

export function NotificationsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { isAuthenticated } = useAuth();

  const [items, setItems] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [toasts, setToasts] = useState<AppNotification[]>([]);

  // Notiser vi redan sett. Första hämtningen fyller den här utan att poppa
  // upp något — annars hade en inloggning spammat skärmen med gammalt.
  const seenIdsRef = useRef<Set<string>>(new Set());
  const hasLoadedRef = useRef(false);
  const toastTimersRef = useRef<Map<string, number>>(new Map());

  const dismissToast = useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id));

    const timer = toastTimersRef.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      toastTimersRef.current.delete(id);
    }
  }, []);

  const pushToasts = useCallback(
    (fresh: AppNotification[]) => {
      if (!fresh.length) return;

      setToasts((current) =>
        [...fresh, ...current].slice(0, MAX_VISIBLE_TOASTS)
      );

      fresh.forEach((notification) => {
        const timer = window.setTimeout(() => {
          dismissToast(notification.id);
        }, TOAST_DURATION_MS);

        toastTimersRef.current.set(notification.id, timer);
      });
    },
    [dismissToast]
  );

  const refresh = useCallback(async () => {
    try {
      const data = await http.get<NotificationsResponse>("/notifications");

      setItems(data.items);
      setUnreadCount(data.unreadCount);
      setPendingRequests(data.pendingRequests);

      const unseen = data.items.filter(
        (n) => !n.read && !seenIdsRef.current.has(n.id)
      );

      data.items.forEach((n) => seenIdsRef.current.add(n.id));

      if (hasLoadedRef.current) {
        pushToasts(unseen);
      }

      hasLoadedRef.current = true;
    } catch (e) {
      console.error("notifications fetch failed", e);
    }
  }, [pushToasts]);

  useEffect(() => {
    if (!isAuthenticated) {
      setItems([]);
      setUnreadCount(0);
      setPendingRequests(0);
      setToasts([]);
      seenIdsRef.current = new Set();
      hasLoadedRef.current = false;
      return;
    }

    let alive = true;

    const load = async () => {
      if (!alive) return;
      await refresh();
    };

    setIsLoading(true);
    load().finally(() => {
      if (alive) setIsLoading(false);
    });

    const interval = window.setInterval(load, POLL_INTERVAL_MS);

    return () => {
      alive = false;
      window.clearInterval(interval);
    };
  }, [isAuthenticated, refresh]);

  useEffect(() => {
    const timers = toastTimersRef.current;

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const markAllRead = useCallback(async () => {
    if (!unreadCount) return;

    // Optimistiskt: badgen ska släckas direkt när dropdownen öppnas.
    setItems((current) => current.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);

    try {
      await http.post("/notifications/read", {});
    } catch (e) {
      console.error("mark notifications read failed", e);
      await refresh();
    }
  }, [unreadCount, refresh]);

  const value = useMemo<NotificationsCtx>(
    () => ({
      items,
      unreadCount,
      pendingRequests,
      isLoading,
      toasts,
      dismissToast,
      refresh,
      markAllRead,
    }),
    [
      items,
      unreadCount,
      pendingRequests,
      isLoading,
      toasts,
      dismissToast,
      refresh,
      markAllRead,
    ]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useNotifications() {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error("useNotifications must be used within <NotificationsProvider>");
  }
  return ctx;
}
